/**
 * Панель фильтров. Набор — паритет с конкурентом плюс то, чего у него нет:
 * порог ROI, возраст, дисциплины, площадки и переключатель помеченных вилок.
 *
 * Дисциплины и площадки берутся из самой ленты, а не из захардкоженного списка:
 * иначе после подключения нового источника его пришлось бы «разрешать» правкой
 * фронта, и вилки по нему тихо не показывались бы.
 */

import { useMemo } from 'react';

import { type SortKey, facets, useFeed } from '../store/feed';
import { familyLabel, marketPrefix, sportLabel } from '../lib/protocol';

const SORTS: { key: SortKey; label: string; hint: string }[] = [
  { key: 'roi', label: 'по ROI', hint: 'сначала самые прибыльные' },
  { key: 'age', label: 'свежие', hint: 'сначала только что появившиеся' },
  {
    key: 'sv',
    label: 'живучие',
    hint: 'сначала те, что обычно держатся дольше — успеть до второй конторы',
  },
];

export function Filters() {
  const filters = useFeed((s) => s.filters);
  const setFilters = useFeed((s) => s.setFilters);
  const bank = useFeed((s) => s.bank);
  const setBank = useFeed((s) => s.setBank);
  const sound = useFeed((s) => s.sound);
  const soundRoi = useFeed((s) => s.soundRoi);
  const setSound = useFeed((s) => s.setSound);
  const setSoundRoi = useFeed((s) => s.setSoundRoi);
  // Фасеты — производная от ленты, и считать её надо здесь: селектор, возвращающий
  // новый объект, для zustand 5 означает вечно меняющееся состояние и пустую страницу.
  const forks = useFeed((s) => s.forks);
  const available = useMemo(() => facets({ forks }), [forks]);
  // Семейства рынков тоже из ленты: захардкоженный список означал бы, что новый
  // рынок с бэкенда молча не показывается, пока не выкатят фронт.
  const families = useMemo(() => {
    const found = new Set<string>();
    for (const fork of forks.values()) {
      const family = marketPrefix(fork.market);
      if (family) found.add(family);
    }
    return [...found].sort();
  }, [forks]);

  const toggle = (list: string[], value: string): string[] =>
    list.includes(value) ? list.filter((item) => item !== value) : [...list, value];

  return (
    <div className="filters">
      <label className="field">
        ROI от
        <input
          type="number"
          step="0.5"
          min="-100"
          value={filters.minRoi}
          onChange={(e) => setFilters({ minRoi: Number.parseFloat(e.target.value) || 0 })}
        />
        %
      </label>

      <label className="field">
        возраст до
        <input
          type="number"
          step="10"
          min="0"
          value={filters.maxAge}
          onChange={(e) => setFilters({ maxAge: Number.parseInt(e.target.value, 10) || 0 })}
        />
        с
      </label>

      <label className="field">
        банк
        <input
          type="number"
          step="50"
          min="1"
          value={bank}
          onChange={(e) => setBank(Number.parseFloat(e.target.value))}
        />
        $
      </label>

      {/* Порог объёма, а не ROI: вилка с ROI 22% и стаканом на 13 долларов —
          это не вилка на ваш банк, а строка, отнимающая время. Свободные фиды
          лимитов не отдают вовсе, поэтому конкурент учит проверять их руками. */}
      <label className="field" title="показывать только те, куда влезает эта сумма">
        влезает от
        <input
          type="number"
          step="50"
          min="0"
          value={filters.minVol}
          onChange={(e) => setFilters({ minVol: Number.parseFloat(e.target.value) || 0 })}
        />
        $
      </label>

      {available.sports.length > 1 && (
        <div className="chips">
          <span className="chip-label">дисциплина</span>
          {available.sports.map((sport) => (
            <button
              key={sport}
              type="button"
              className="chip"
              aria-pressed={filters.sports.includes(sport)}
              onClick={() => setFilters({ sports: toggle(filters.sports, sport) })}
            >
              {sportLabel(sport)}
            </button>
          ))}
        </div>
      )}

      {families.length > 1 && (
        <div className="chips">
          <span className="chip-label">рынок</span>
          {families.map((family) => (
            <button
              key={family}
              type="button"
              className="chip"
              aria-pressed={filters.markets.includes(family)}
              onClick={() => setFilters({ markets: toggle(filters.markets, family) })}
              title="рынки отменяются по-разному: тотал карт снимут при переносе, победа в матче доживёт"
            >
              {familyLabel(family)}
            </button>
          ))}
        </div>
      )}

      {available.sources.length > 1 && (
        <div className="chips">
          <span className="chip-label">площадки</span>
          {available.sources.map((source) => (
            <button
              key={source}
              type="button"
              className="chip"
              aria-pressed={filters.sources.includes(source)}
              onClick={() => setFilters({ sources: toggle(filters.sources, source) })}
            >
              {source}
            </button>
          ))}
          {/* Пока площадки не выбраны, требовать «обе мои» не от чего. */}
          {filters.sources.length > 0 && (
            <button
              type="button"
              className="chip"
              aria-pressed={filters.bothLegs}
              onClick={() => setFilters({ bothLegs: !filters.bothLegs })}
              title="обе ноги в выбранных площадках: вилка в конторе без аккаунта — не вилка"
            >
              обе ноги мои
            </button>
          )}
        </div>
      )}

      <div className="chips">
        <span className="chip-label">порядок</span>
        {SORTS.map((option) => (
          <button
            key={option.key}
            type="button"
            className="chip"
            aria-pressed={filters.sort === option.key}
            onClick={() => setFilters({ sort: option.key })}
            title={option.hint}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="chips">
        {/* Переключателя «с пометками» здесь больше нет, и это не упрощение
            интерфейса, а следствие бинарного гейта: вилка либо в ленте, либо
            снята с названной причиной. Оставить кнопку, которая ничего не
            фильтрует, значило бы обещать выбор, которого нет. */}
        {/* Переключатель сам по себе является тем кликом, после которого браузер
            разрешает звук: включать его заранее без спроса нельзя. */}
        <button
          type="button"
          className="chip"
          aria-pressed={sound}
          onClick={() => setSound(!sound)}
          title="короткий сигнал, когда в ленту приезжает новая вилка"
        >
          звук
        </button>
        {sound && (
          <label className="field" title="ниже этого ROI сигнала не будет">
            от
            <input
              type="number"
              step="0.5"
              min="0"
              value={soundRoi}
              onChange={(e) => setSoundRoi(Number.parseFloat(e.target.value) || 0)}
            />
            %
          </label>
        )}
      </div>
    </div>
  );
}
